<template>
  <div class="musubi-page musubi-page--pipelines">
    <header class="musubi-page__head">
      <h1>Pipelines</h1>
      <p class="text-muted">
        Movement against your targets, this period. Describe a new campaign in a sentence, watch
        your running pipelines catch and enrol matches, and keep an eye on your usage — all in one place.
      </p>
    </header>

    <!-- Auto-prompt: finish setting up (onboarding slice 2b). Dismissible per
         session, returns until the required steps are done. -->
    <div v-if="showSetupBanner" class="setup-banner">
      <div class="sb-ic"><CygnusIcon name="rocket" /></div>
      <div class="sb-body">
        <div class="sb-title">Finish setting up — {{ setupStatus.required_remaining }} step{{ setupStatus.required_remaining === 1 ? '' : 's' }} left</div>
        <div class="sb-sub">Complete the essentials so Yorlin can start finding and contacting businesses for you.</div>
        <div class="sb-mini">
          <i v-for="(s, i) in setupStatus.steps" :key="i" :class="{ done: s.resolved }"></i>
        </div>
      </div>
      <div class="sb-act">
        <a href="#" class="dismiss" @click.prevent="dismissSetup">Later</a>
        <button class="btn btn-pri btn-sm" @click="$router.push('/setup')">Resume setup →</button>
      </div>
    </div>

    <div class="ph-grid">
      <!-- ═══ CENTRE ═══ -->
      <div class="ph-main">

        <!-- AI hero prompt (shared component) -->
        <AiPromptPanel
          v-model="promptText"
          variant="hero"
          title="Start a campaign in a sentence"
          subtitle="We draft it — you review before anything runs."
          :examples="examplePrompts"
          :generating="generating"
          :error="generateError"
          :cost-text="costText"
          @generate="generate"
        />

        <!-- Tailored starting points. The generic examples above teach the AI box
             what a good brief looks like; these are concrete first campaigns
             worked out from this workspace's own sector and targeting regions -
             a builder in Swindon gets "Concrete suppliers in Swindon", not
             "Accountants in Leeds". Deterministic and AI-free on both sides:
             each chip is a plain deep-link that opens the wizard with the
             vertical and the town already chosen. Renders nothing at all when
             the endpoint has nothing to say. -->
        <div v-if="suggestions.length" class="ph-sugg">
          <div class="ph-sugg__h">
            <span class="ph-sugg__title"><CygnusIcon name="pin" class="me-1" />Or start from one of these</span>
            <span class="ph-sugg__sub">{{ suggestCaption }}</span>
          </div>
          <div class="ph-sugg__chips">
            <a
              v-for="(s, i) in suggestions" :key="i"
              :href="suggestHref(s)" class="ph-sugg__chip"
            >{{ s.label }}</a>
          </div>
        </div>

        <!-- Your pipelines -->
        <div class="ph-sec-h">
          <h2>Your pipelines</h2>
          <span class="ph-count">{{ pipelinesCaption }}</span>
          <span class="ph-spacer"></span>
          <a href="/acquire/pipeline-wizard" class="btn btn-sm btn-primary">＋ New pipeline</a>
        </div>

        <!-- Draft pipelines nudge — the wizard/generator leave a NEW pipeline as a
             draft under Drip flows, and the movement cards below only show live
             ones, so a freshly built pipeline used to be invisible here. Surface
             drafts with a direct "review & activate" deep-link (findability fix). -->
        <div v-if="draftFlows.length" class="ph-drafts-card">
          <div class="ph-drafts-card__head">
            <div class="ph-drafts-card__ic"><CygnusIcon name="rocket" /></div>
            <div class="ph-drafts-card__copy">
              <div class="ph-drafts-card__title">{{ draftFlows.length }} pipeline{{ draftFlows.length === 1 ? '' : 's' }} in draft</div>
              <div class="ph-drafts-card__sub">Draft pipelines don't send yet - review and activate one to start sending. Built one in the wizard? It lands here as a draft.</div>
            </div>
          </div>
          <ul class="ph-drafts-list">
            <li v-for="f in draftFlows" :key="f.id" class="ph-drafts-row">
              <span class="ph-drafts-row__name">{{ f.name }}</span>
              <MusubiStatusPill status="draft" />
              <a :href="`/outreach/drip-flows?flow=${f.id}`" class="btn btn-sm btn-primary ph-drafts-row__cta">Review &amp; activate →</a>
            </li>
          </ul>
        </div>

        <div v-if="pipelinesError" class="alert alert-warning py-2 small">{{ pipelinesError }}</div>
        <div v-else-if="loadingPipelines" class="ph-empty">Loading your pipelines…</div>

        <div v-else class="ph-pipe-grid">
          <article
            v-for="p in pipelines"
            :key="p.id"
            class="pcard"
            :class="{ 'pcard--paused': p.status === 'paused' }"
          >
            <div class="pcard__top">
              <div class="pcard__target">{{ p.name }}</div>
              <MusubiStatusPill :status="p.status" />
            </div>
            <div class="pcard__meta">{{ p.audience_summary }}</div>

            <!-- DISCOVERY MODE — gathering, not sending: show building counts. -->
            <template v-if="p.status === 'discovering' && p.discovery">
              <div class="pcard__cap pcard__cap--disc"><CygnusIcon name="search" class="me-1" />Discovering — not sending yet</div>
              <div class="disc-band">
                <div class="disc-band__c"><span class="disc-band__v">{{ fmtN(p.discovery.discovered) }}</span><span class="disc-band__k">discovered</span></div>
                <div class="disc-band__c"><span class="disc-band__v">{{ fmtN(p.discovery.enriched) }}</span><span class="disc-band__k">enriched</span></div>
                <div class="disc-band__c disc-band__c--elig"><span class="disc-band__v">{{ fmtN(p.discovery.would_be_eligible) }}<span v-if="p.discovery.capped">+</span></span><span class="disc-band__k">would enrol</span></div>
              </div>
              <p class="disc-band__note">{{ p.discovery.go_live_note }}</p>
            </template>

            <template v-else>
              <div class="pcard__cap">
                {{ funnelFor(p).ghost ? 'Movement while active' : 'Movement this period' }}
                <span v-if="funnelFor(p).ghost" class="pcard__ghost-tag">paused — last active</span>
              </div>
              <div class="funnel" :class="{ 'funnel--ghost': funnelFor(p).ghost }">
                <div v-for="(cell, i) in funnelCells(p)" :key="cell.key" class="fn" :class="`fn--s${i + 1}`">
                  <div class="fn__v">{{ cell.value }}</div>
                  <div class="fn__k">{{ cell.label }}</div>
                  <div class="fn__bar" :style="{ width: cell.barPct + '%' }"></div>
                </div>
              </div>
            </template>

            <div class="pcard__actions">
              <button
                v-if="p.status === 'active'"
                class="btn btn-sm btn-link ph-act"
                :disabled="busyId === p.id"
                @click="setStatus(p, 'pause')"
              ><CygnusIcon name="pause" class="me-1" />Pause</button>
              <button
                v-else-if="p.status === 'discovering'"
                class="btn btn-sm btn-link ph-act ph-act--go"
                :disabled="busyId === p.id"
                @click="setStatus(p, 'activate')"
              ><CygnusIcon name="rocket" class="me-1" />Go live</button>
              <button
                v-else
                class="btn btn-sm btn-link ph-act ph-act--go"
                :disabled="busyId === p.id"
                @click="setStatus(p, 'activate')"
              ><CygnusIcon name="play" class="me-1" />Resume</button>
              <a :href="`/outreach/drip-flows?flow=${p.id}`" class="btn btn-sm btn-link ph-act">Edit targets</a>
              <span class="ph-spacer"></span>
              <button
                class="btn btn-sm btn-link ph-act"
                :class="{ 'ph-act--go': openRecipientsId === p.id }"
                @click="toggleRecipients(p)"
              ><CygnusIcon name="users" class="me-1" />{{ openRecipientsId === p.id ? 'Hide recipients' : 'Recipients' }}</button>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="exportingId === p.id"
                :title="fullExport ? 'Download a CSV of everyone this pipeline currently matches' : 'Exports contacts who\'ve engaged with your campaigns (opened, clicked or replied)'"
                @click="exportMatches(p)"
              ><CygnusIcon name="spinner" :spin="true" class="me-1" v-if="exportingId === p.id" />{{ fullExport ? 'Export matches' : 'Export engaged' }}</button>
              <a :href="matchesLink(p)" class="btn btn-sm btn-outline-secondary">View matches</a>
            </div>
            <p v-if="!fullExport" class="pcard__gatehint">
              <CygnusIcon name="lock" /> Full match export (all matched contacts) is an Enterprise feature.
            </p>

            <!-- Recipients drill-down (recipient intelligence): who this pipeline
                 has actually sent to + how each interacted. -->
            <div v-if="openRecipientsId === p.id" class="pcard__recipients">
              <RecipientDrillDown :endpoint="`/api/v1/admin/pipelines/${p.id}/recipients`" :show-step="true" />
            </div>
          </article>

          <!-- New pipeline CTA card -->
          <a href="/acquire/pipeline-wizard" class="pcard pcard--new">
            <span class="pcard--new__plus">＋</span>
            <span class="pcard--new__h">New pipeline</span>
            <span class="pcard--new__p">Tell us who to find — we watch for matches and enrol them automatically.</span>
            <span class="btn btn-sm btn-primary">Start the wizard →</span>
          </a>
        </div>

        <p v-if="draftsCount > 0" class="ph-drafts-link">
          <a href="/acquire/drafts"><CygnusIcon name="bolt" class="me-1" />{{ draftsCount }} AI draft{{ draftsCount === 1 ? '' : 's' }} waiting for review →</a>
        </p>

        <!-- Active campaigns strip -->
        <div class="ph-sec-h ph-sec-h--camp">
          <h2>Active campaigns</h2>
          <span class="ph-count">{{ campaignsCaption }}</span>
        </div>
        <div v-if="campaignsError" class="alert alert-warning py-2 small">{{ campaignsError }}</div>
        <div v-else-if="!loadingCampaigns && activeCampaigns.length === 0" class="ph-empty">
          No one-off campaigns running. Your pipelines above enrol matches automatically.
        </div>
        <div v-else class="ph-camps">
          <div v-for="c in activeCampaigns" :key="c.id" class="camp">
            <div class="camp__id">
              <div class="camp__name">{{ c.name }}</div>
              <div class="camp__sub">One-off send · {{ c.total_recipients.toLocaleString() }} recipients · {{ campStatusText(c) }}</div>
            </div>
            <div v-for="m in campMetrics(c)" :key="m.key" class="metric">
              <div class="metric__top"><span class="metric__k">{{ m.label }}</span><span class="metric__v">{{ m.pct }}%</span></div>
              <div class="metric__bar"><span :class="`fill--${m.key}`" :style="{ width: Math.min(100, m.pct * 2) + '%' }"></span></div>
            </div>
            <a :href="`/outreach/campaigns`" class="btn btn-sm btn-outline-secondary camp__act">View</a>
          </div>
        </div>
      </div>

      <!-- ═══ RAIL ═══ -->
      <aside class="ph-rail">
        <div class="rail-card">
          <div class="rail-card__h">
            Usage this period
            <span v-if="usage" class="rail-card__reset">resets {{ resetsText }}</span>
          </div>
          <div v-if="usageError" class="alert alert-warning py-2 small mb-0">{{ usageError }}</div>
          <div v-else-if="!usage" class="ph-empty">Loading…</div>
          <template v-else>
            <div v-for="meter in railMeters" :key="meter.key" class="rail-meter">
              <MusubiUsageMeter :label="meter.label" :used="meter.used" :quota="meter.quota" />
              <div class="rail-meter__foot">
                <span>{{ meterCaption(meter) }}</span>
                <a v-if="meter.addon" href="#" @click.prevent="buyMeter = meter.key" class="rail-meter__addon">{{ meter.addon }}</a>
              </div>
            </div>
          </template>
        </div>

        <BuyCapacity
          v-if="buyMeter"
          :meter="buyMeter"
          @close="buyMeter = null"
          @purchased="onPurchased"
        />

        <div v-if="usage" class="plan-card">
          <div class="plan-card__top">
            <span class="plan-card__name">{{ usage.plan.name || '—' }}</span>
            <span class="plan-card__badge">Current plan</span>
          </div>
          <div class="plan-card__renew">Resets {{ resetsText }}</div>
          <div v-if="usage.plan.can_manage" class="plan-card__foot">
            <a href="/admin/usage-plans">Manage plans</a>
          </div>
        </div>

        <!-- Quick links — light pointers to the key surfaces so the home isn't a
             dead-end for someone hunting for prospects, flows, templates, etc. -->
        <nav class="quicklinks-card" aria-label="Go to">
          <div class="quicklinks-card__h">Go to</div>
          <a class="quicklink" href="/acquire/prospects"><CygnusIcon name="users" class="quicklink__ic" /><span>Prospects</span><CygnusIcon name="chevron-right" class="quicklink__go" /></a>
          <a class="quicklink" href="/outreach/drip-flows"><CygnusIcon name="diagram" class="quicklink__ic" /><span>Drip flows</span><CygnusIcon name="chevron-right" class="quicklink__go" /></a>
          <a class="quicklink" href="/admin/email-templates"><CygnusIcon name="envelope-open-text" class="quicklink__ic" /><span>Email templates</span><CygnusIcon name="chevron-right" class="quicklink__go" /></a>
          <a v-if="usage?.features?.direct_mail" class="quicklink" href="/outreach/direct-mail"><CygnusIcon name="box" class="quicklink__ic" /><span>Direct Mail</span><CygnusIcon name="chevron-right" class="quicklink__go" /></a>
          <a class="quicklink" href="/outreach/deliverability"><CygnusIcon name="shield" class="quicklink__ic" /><span>Deliverability</span><CygnusIcon name="chevron-right" class="quicklink__go" /></a>
        </nav>
      </aside>
    </div>
  </div>
</template>

<script>
/**
 * PipelinesHome — the reshaped, movement-first home (usability-refocus slice 4 /
 * plan §1). Mounts at `/` via DashboardHomeAlterHook, replacing MarketingOverview.
 *
 * Three data sources, each degrading independently so one 403 never blanks the
 * page (the home is "any authenticated"; sections gate per their own API):
 *   - GET /api/v1/me/usage        → usage rail + plan card (always available)
 *   - GET /api/v1/admin/pipelines → per-pipeline THIS-PERIOD movement funnels
 *   - GET /api/v1/admin/campaigns → active one-off campaigns strip (movement)
 *
 * NO corpus stock numbers anywhere — movement only. Paused pipelines show honest
 * "while active" ghost figures from the endpoint rather than dead zeros. The AI
 * hero reuses the shared AiPromptPanel (same surface as the wizard).
 */
import useApi from "@apexl/icarus-core/src/services/api";
import { apiErrorMessage } from "./util/aiError";
import { addonForMeter, addonPriceText, loadAddonCatalogue } from "./util/addonPrice";
import AiPromptPanel from "@root/components/ui/AiPromptPanel.vue";
import BuyCapacity from "@root/components/ui/BuyCapacity.vue";
import MusubiStatusPill from "@root/components/ui/MusubiStatusPill.vue";
import MusubiUsageMeter from "@root/components/ui/MusubiUsageMeter.vue";
import RecipientDrillDown from "@root/components/ui/RecipientDrillDown.vue";

const METER_META = {
  // The add-on link's price comes from the add-on catalogue (Hermes's charge,
  // VAT included), never a constant here.
  ai_tokens:        { label: 'AI tokens',        addonWhenCapped: 'Add 1,000' },
  emails:           { label: 'Emails this period', addonWhenCapped: 'Add 1,000' },
  active_prospects: { label: 'Active prospects' },
  sending_domains:  { label: 'Sending domains', addonWhenCapped: 'Add domain' },
};
const METER_ORDER = ['ai_tokens', 'emails', 'active_prospects', 'sending_domains'];
const FUNNEL_KEYS = [
  { key: 'new_matches', label: 'New matches' },
  { key: 'enrolled',    label: 'Enrolled' },
  { key: 'opened',      label: 'Opened' },
  { key: 'replied',     label: 'Replied' },
];

export default {
  name: 'PipelinesHome',
  components: { AiPromptPanel, BuyCapacity, MusubiStatusPill, MusubiUsageMeter, RecipientDrillDown },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),

      // AI hero (generic service-seller examples only — never decay signals)
      promptText: '',
      generating: false,
      generateError: '',
      examplePrompts: [
        'IT companies in Gloucestershire',
        'Accountants in Leeds, 5+ staff',
        'Wellness studios in Bath',
      ],
      costText: 'A draft uses a few AI tokens (≈ 15–40). Nothing sends — you review the draft first.',

      // Tailored starting points (GET /admin/campaign-suggestions) — no AI, no cost.
      suggestions: [],
      suggestIndustry: null,
      suggestLocation: '',
      suggestGeneric: false,

      pipelines: [],
      // All the tenant's drip flows (any status) — the /pipelines endpoint only
      // returns live watchers (active/paused/discovering with an audience), so a
      // freshly built DRAFT pipeline is invisible there. We read the full flows
      // list to surface drafts with a direct review link (findability fix).
      allFlows: [],
      draftsCount: 0,
      loadingPipelines: true,
      pipelinesError: '',
      busyId: null,
      exportingId: null,
      openRecipientsId: null,

      campaigns: [],
      loadingCampaigns: true,
      campaignsError: '',

      usage: null,
      usageError: '',
      buyMeter: null,
      addonCatalogue: {},
      addonCurrency: 'GBP',

      setupStatus: null,
      setupDismissed: false,
    };
  },
  computed: {
    /** Says WHY these six, so a tailored set never reads as a random list. */
    suggestCaption() {
      const where = this.suggestLocation ? ` in ${this.suggestLocation}` : '';
      if (this.suggestIndustry && !this.suggestGeneric) {
        return `Picked for a ${this.suggestIndustry.label.toLowerCase()}${where}. No AI needed - each one opens the wizard ready to go.`;
      }
      return `Popular places to start${where}. No AI needed - each one opens the wizard ready to go.`;
    },
    showSetupBanner() {
      return !!this.setupStatus && !this.setupStatus.complete && !this.setupDismissed;
    },
    pipelinesCaption() {
      const running = this.pipelines.filter(p => p.status === 'active').length;
      const paused = this.pipelines.filter(p => p.status === 'paused').length;
      const bits = [];
      if (running) bits.push(`${running} running`);
      if (paused) bits.push(`${paused} paused`);
      return bits.length ? bits.join(' · ') : 'No pipelines yet';
    },
    activeCampaigns() {
      // One-off sends that are live or recently sent (movement idiom).
      return this.campaigns
        .filter(c => ['sending', 'scheduled', 'sent', 'paused'].includes(c.status))
        .slice(0, 4);
    },
    campaignsCaption() {
      const n = this.activeCampaigns.length;
      return n ? `${n} one-off send${n === 1 ? '' : 's'}` : '—';
    },
    railMeters() {
      if (!this.usage) return [];
      return METER_ORDER
        .filter(k => this.usage.meters[k])
        .map(k => {
          const m = this.usage.meters[k];
          const meta = METER_META[k] || { label: k };
          const capped = m.quota !== null && m.quota > 0;
          const pct = capped ? Math.round((m.used / m.quota) * 100) : 0;
          return {
            key: k,
            label: meta.label,
            used: m.used,
            quota: m.quota,
            topup: m.topup_available || 0,
            pct,
            // "Add capacity" is offered on any FINITE (capped) meter that has a
            // catalogued add-on — before or at the cap. Unlimited plans hide it.
            addon: (capped && meta.addonWhenCapped) ? this.addonLinkText(k, meta.addonWhenCapped) : '',
          };
        });
    },
    resetsText() {
      const ts = this.usage?.period?.resets_at;
      if (!ts) return '—';
      return new Date(ts * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    },
    // Full match export (all matched contacts, not just engaged) is gated to
    // Enterprise (data_views) + operators (can_manage = Ops::PLATFORM). Standard
    // tenants get the engaged-only export — mirrors the server-side gate.
    fullExport() {
      return !!(this.usage?.features?.data_views || this.usage?.plan?.can_manage);
    },
    /** Draft pipelines (most recent first) — the ones that were invisible on the
     *  home before. Capped at 5 so the nudge stays a pointer, not a full list. */
    draftFlows() {
      return (this.allFlows || [])
        .filter(f => f.status === 'draft')
        .slice(0, 5);
    },
  },
  mounted() {
    this.loadUsage();
    this.loadAddonPrices();
    this.loadPipelines();
    this.loadFlowsSummary();
    this.loadCampaigns();
    this.loadSetup();
    this.loadSuggestions();
  },
  methods: {
    /**
     * Tailored starting points. Deliberately silent on failure: this block is an
     * accelerator, never a dependency, so anything short of a real list simply
     * removes it from the page.
     */
    async loadSuggestions() {
      try {
        const res = await this.api.get('/api/v1/admin/campaign-suggestions');
        const p = res?.data?.data !== undefined ? res.data : res;
        this.suggestions     = Array.isArray(p?.data) ? p.data : [];
        this.suggestIndustry = p?.industry || null;
        this.suggestLocation = p?.location || '';
        this.suggestGeneric  = !!p?.generic;
      } catch (e) {
        this.suggestions = [];
      }
    },
    /** Deep-link into the wizard with the vertical (preset slug or free text) + town preselected. */
    suggestHref(s) {
      const q = new URLSearchParams();
      if (s.business_type_slug) q.set('bt', s.business_type_slug);
      else if (s.query) q.set('q', s.query);
      if (s.location) q.set('location', s.location);
      return `/acquire/pipeline-wizard?${q.toString()}`;
    },
    async loadSetup() {
      this.setupDismissed = sessionStorage.getItem('musubi.setup.dismissed') === '1';
      try {
        const res = await this.api.get('/api/v1/me/setup-status');
        this.setupStatus = res?.data?.data ?? res?.data ?? res;
      } catch (e) { this.setupStatus = null; }
    },
    dismissSetup() {
      this.setupDismissed = true;
      try { sessionStorage.setItem('musubi.setup.dismissed', '1'); } catch (e) { /* ignore */ }
    },
    /** Add-on prices for the usage rail links. Silent on failure: no price shown. */
    async loadAddonPrices() {
      const { catalogue, currency } = await loadAddonCatalogue(this.api);
      this.addonCatalogue = catalogue;
      this.addonCurrency = currency;
    },
    addonLinkText(meterKey, prefix) {
      const price = addonPriceText(addonForMeter(this.addonCatalogue, meterKey), this.addonCurrency);
      return price ? `${prefix} · ${price}` : prefix;
    },
    async loadUsage() {
      try {
        const res = await this.api.get('/api/v1/me/usage');
        this.usage = res?.data?.data ?? res?.data ?? res;
      } catch (e) {
        this.usageError = this.errText(e, 'Usage is unavailable right now.');
      }
    },
    async loadPipelines() {
      this.loadingPipelines = true;
      try {
        const res = await this.api.get('/api/v1/admin/pipelines');
        // processResponse returns the body ({data,drafts_count,period}); the
        // 403-retry path can double-wrap it under `.data` — handle both.
        const body = res?.data?.data !== undefined ? res.data : res;
        this.pipelines = body?.data ?? [];
        this.draftsCount = body?.drafts_count ?? 0;
      } catch (e) {
        this.pipelinesError = e?.response?.status === 403
          ? 'Your pipelines need Outreach access — ask an admin to enable it.'
          : this.errText(e, 'Could not load your pipelines.');
      } finally {
        this.loadingPipelines = false;
      }
    },
    /** All drip flows (any status) for the drafts nudge. Reuses the existing
     *  Outreach-gated list endpoint; best-effort — a 403/other error just leaves
     *  the drafts card hidden (the movement pipelines below are unaffected). */
    async loadFlowsSummary() {
      try {
        const res = await this.api.get('/api/v1/admin/drip-flows');
        const body = res?.data?.data ?? res?.data ?? res;
        const list = Array.isArray(body) ? body : (body?.rows || body?.data || []);
        this.allFlows = Array.isArray(list) ? list : [];
      } catch (e) { this.allFlows = []; }
    },
    async loadCampaigns() {
      this.loadingCampaigns = true;
      try {
        const res = await this.api.get('/api/v1/admin/campaigns');
        const body = res?.data?.data !== undefined ? res.data : res;
        this.campaigns = body?.data ?? [];
      } catch (e) {
        this.campaignsError = e?.response?.status === 403
          ? 'Campaigns need Outreach access.'
          : this.errText(e, 'Could not load campaigns.');
      } finally {
        this.loadingCampaigns = false;
      }
    },
    /** AI path: POST the brief → generate a PipelineDraft → open its review page. */
    async generate() {
      const prompt = this.promptText.trim();
      if (!prompt || this.generating) return;
      this.generating = true;
      this.generateError = '';
      try {
        const res = await this.api.post('/api/v1/admin/drafts', { prompt });
        const p = res?.data ?? res;
        const id = p?.draft?.id;
        if (!id) throw new Error('The generator returned no draft.');
        window.location.href = `/acquire/drafts/${id}`;
      } catch (e) {
        this.generating = false;
        this.generateError = apiErrorMessage(e, 'Could not generate a draft just now.');
      }
    },
    async setStatus(p, action) {
      this.busyId = p.id;
      try {
        await this.api.post(`/api/v1/admin/drip-flows/${p.id}/${action}`, {});
        p.status = action === 'pause' ? 'paused' : 'active';
        // Going live from discovery flips the card to its movement funnel.
        if (action === 'activate') p.discovery = null;
      } catch (e) {
        this.pipelinesError = e?.response?.data?.error || e?.message || `Could not ${action} the pipeline.`;
      } finally {
        this.busyId = null;
      }
    },
    /** The movement object a card should render: current, or ghost when paused+empty.
     *  The endpoint only populates `ghost` for a paused pipeline whose current
     *  period is empty, so preferring it here is the honest "while active" view. */
    funnelFor(p) {
      return { data: p.ghost || p.movement, ghost: !!p.ghost };
    },
    funnelCells(p) {
      const f = this.funnelFor(p);
      const src = f.data || {};
      const max = Math.max(1, ...FUNNEL_KEYS.map(k => src[k.key] || 0));
      return FUNNEL_KEYS.map(k => ({
        key: k.key,
        label: k.label,
        value: src[k.key] || 0,
        barPct: Math.round(((src[k.key] || 0) / max) * 100),
      }));
    },
    fmtN(n) { return Number(n || 0).toLocaleString(); },
    toggleRecipients(p) {
      this.openRecipientsId = this.openRecipientsId === p.id ? null : p.id;
    },
    matchesLink(p) {
      // "View matches" → the prospects browser, deep-linked so it pre-applies
      // this pipeline's first audience source to the visible top-bar controls
      // (see AdminLeads.vue's applyPipelineParam). For the EXACT matching set
      // (every audience source, unioned) use "Export matches" instead.
      return `/acquire/prospects?pipeline=${p.id}`;
    },
    /**
     * "Export matches" — CSV of everyone this pipeline currently matches
     * (every audience source, unioned + deduped server-side; see
     * AdminPipelineExportController). Authenticated blob download: useApi()'s
     * axios instance already attaches the Bearer token, so a plain <a href>
     * won't work (no way to carry the header) — fetch as a blob and trigger
     * the browser download via an object URL instead.
     */
    async exportMatches(p) {
      if (this.exportingId) return;
      this.exportingId = p.id;
      try {
        const res = await this.api.get(`/api/v1/admin/pipelines/${p.id}/export.csv`, { responseType: 'blob' });
        // useApi()'s processResponse normally unwraps {data} → data; on the
        // 403-retry path it can double-wrap once more. Handle both so a blob
        // comes out either way.
        const blob = res instanceof Blob ? res : (res?.data instanceof Blob ? res.data : res);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `pipeline-${p.id}-matches.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.pipelinesError = this.errText(e, `Could not export matches for "${p.name}".`);
      } finally {
        this.exportingId = null;
      }
    },
    campMetrics(c) {
      const e = c.engagement || { sent: 0, opens: 0, clicks: 0, replies: 0 };
      const base = e.sent || c.total_recipients || 0;
      const pct = (n) => base > 0 ? Math.round((n / base) * 100) : 0;
      return [
        { key: 'open',  label: 'Open',  pct: pct(e.opens) },
        { key: 'click', label: 'Click', pct: pct(e.clicks) },
        { key: 'reply', label: 'Reply', pct: pct(e.replies) },
      ];
    },
    campStatusText(c) {
      if (c.status === 'sending') return 'sending now';
      if (c.status === 'scheduled') return 'scheduled';
      if (c.completed_at) return 'sent ' + new Date(c.completed_at * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
      return c.status;
    },
    meterCaption(meter) {
      if (meter.quota === null) return `${meter.used.toLocaleString()} used · no cap`;
      const base = `${meter.used.toLocaleString()} / ${meter.quota.toLocaleString()} · ${meter.pct}% used`;
      return meter.topup > 0 ? `${base} · +${meter.topup.toLocaleString()} top-up` : base;
    },
    onPurchased() {
      // A top-up credit / new slot changes the meters — reload the rail.
      this.buyMeter = null;
      this.loadUsage();
    },
    errText(e, fallback) {
      return e?.response?.data?.error || e?.message || fallback;
    },
  },
};
</script>

<style>
/* Reuses the shared --m-* design tokens (see MarketingOverview). */
.musubi-page--pipelines { padding: var(--m-space-6, 32px); color: var(--m-text-primary); }

/* Setup auto-prompt banner (onboarding slice 2b). */
.musubi-page--pipelines .setup-banner { display: flex; align-items: center; gap: 14px; background: var(--m-bg-card, #fff); border: 1px solid #DDE1FF; border-left: 3px solid var(--m-accent, #4F46E5); border-radius: 12px; box-shadow: var(--m-shadow-card); padding: 14px 16px; margin-bottom: 22px; }
.musubi-page--pipelines .setup-banner .sb-ic { width: 38px; height: 38px; border-radius: 10px; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent, #4F46E5); display: flex; align-items: center; justify-content: center; font-size: 1.05rem; flex: none; }
.musubi-page--pipelines .setup-banner .sb-body { flex: 1; min-width: 0; }
.musubi-page--pipelines .setup-banner .sb-title { font-size: .92rem; font-weight: 700; letter-spacing: -.01em; }
.musubi-page--pipelines .setup-banner .sb-sub { font-size: .78rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 2px; }
.musubi-page--pipelines .setup-banner .sb-mini { display: flex; gap: 5px; margin-top: 8px; }
.musubi-page--pipelines .setup-banner .sb-mini i { width: 26px; height: 5px; border-radius: 999px; background: #E4E7F5; }
.musubi-page--pipelines .setup-banner .sb-mini i.done { background: var(--m-success, #0D9488); }
.musubi-page--pipelines .setup-banner .sb-act { flex: none; display: flex; align-items: center; gap: 10px; }
.musubi-page--pipelines .setup-banner .dismiss { color: var(--m-text-tertiary, #9AA0AC); font-size: .78rem; text-decoration: none; font-weight: 600; }
.musubi-page--pipelines .setup-banner .btn { display: inline-flex; align-items: center; gap: 6px; font: inherit; font-weight: 650; border-radius: 8px; padding: 7px 12px; font-size: .8rem; cursor: pointer; border: 1px solid transparent; background: var(--m-accent, #4F46E5); color: #fff; }
.musubi-page--pipelines .musubi-page__head h1 { margin: 0 0 4px; font-size: 1.6rem; letter-spacing: -0.01em; font-weight: 750; }
.musubi-page--pipelines .musubi-page__head .text-muted { color: var(--m-text-secondary); margin-bottom: 24px; max-width: 78ch; }

.ph-grid { display: grid; grid-template-columns: 1fr 300px; gap: 24px; align-items: start; }
@media (max-width: 1080px) { .ph-grid { grid-template-columns: 1fr; } }

.ph-sec-h { display: flex; align-items: baseline; gap: 10px; margin: 6px 0 14px; }
.ph-sec-h h2 { font-size: 1.1rem; font-weight: 700; letter-spacing: -0.01em; margin: 0; }
.ph-sec-h--camp { margin-top: 30px; }
.ph-count { font-size: 0.78rem; color: var(--m-text-tertiary); }
.ph-spacer { margin-left: auto; }
.ph-empty { color: var(--m-text-secondary); font-size: 0.88rem; background: var(--m-bg-content); border: 1px dashed var(--m-border-strong); border-radius: var(--m-radius); padding: 14px; }

/* Pipeline cards */
.ph-pipe-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 700px) { .ph-pipe-grid { grid-template-columns: 1fr; } }
.pcard { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); padding: 18px; display: flex; flex-direction: column; transition: box-shadow .15s, border-color .15s; }
.pcard:hover { border-color: var(--m-border-strong); }
.pcard--paused { background: var(--m-bg-content); }
.pcard__top { display: flex; align-items: flex-start; gap: 10px; }
.pcard__target { font-size: 0.95rem; font-weight: 700; letter-spacing: -0.01em; line-height: 1.35; margin-right: auto; }
.pcard__meta { font-size: 0.74rem; color: var(--m-text-tertiary); margin-top: 5px; }
.pcard__cap { font-size: 0.62rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--m-text-tertiary); margin: 14px 0 6px; display: flex; align-items: center; gap: 8px; }
.pcard__ghost-tag { font-weight: 600; letter-spacing: 0; text-transform: none; color: var(--m-status-warning); }
/* DISCOVERY MODE — teal informational band (not the paused idiom). */
.pcard__cap--disc { color: #0F766E; }
.disc-band { display: flex; gap: 8px; }
.disc-band__c { flex: 1; background: #F0FDFA; border: 1px solid #CCFBF1; border-radius: var(--m-radius, 6px); padding: 8px 6px; text-align: center; }
.disc-band__c--elig { border-color: #5EEAD4; }
.disc-band__v { display: block; font-size: 1.15rem; font-weight: 700; color: #134E4A; line-height: 1.1; }
.disc-band__k { display: block; font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.05em; color: #0F766E; margin-top: 2px; }
.disc-band__note { font-size: 0.74rem; color: #0F766E; margin: 8px 0 0; font-style: italic; }

.funnel { display: flex; background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: var(--m-radius); overflow: hidden; }
.funnel--ghost .fn__v { color: var(--m-text-tertiary); }
.funnel--ghost .fn__bar { background: var(--m-text-tertiary); opacity: .4; }
.fn { flex: 1; padding: 10px 6px 9px; text-align: center; }
.fn + .fn { border-left: 1px solid var(--m-border); }
.fn__v { font-size: 1.15rem; font-weight: 800; letter-spacing: -0.02em; line-height: 1; }
.fn__k { font-size: 0.56rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--m-text-tertiary); margin-top: 4px; }
.fn__bar { height: 3px; border-radius: 999px; margin: 7px auto 0; background: var(--m-accent); opacity: .85; }
.fn--s2 .fn__bar { background: color-mix(in srgb, var(--m-accent) 70%, var(--m-accent-2)); }
.fn--s3 .fn__bar { background: var(--m-accent-2); }
.fn--s4 .fn__bar { background: color-mix(in srgb, var(--m-accent-2) 70%, #000); }

.pcard__actions { display: flex; align-items: center; gap: 4px; margin-top: 13px; padding-top: 13px; border-top: 1px solid var(--m-border); }
.ph-act { font-size: 0.78rem; font-weight: 600; text-decoration: none; padding: 4px 8px; color: var(--m-text-secondary); }
.ph-act--go { color: var(--m-accent); }
.pcard__gatehint { margin: 8px 0 0; font-size: 0.7rem; color: #7C3AED; display: flex; align-items: center; gap: 6px; }
.pcard__recipients { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--m-border); }

.pcard--new { border: 1.5px dashed var(--m-border-strong); background: linear-gradient(135deg, var(--m-accent-soft), #F5F3FF); box-shadow: none; align-items: center; justify-content: center; text-align: center; gap: 8px; padding: 24px 18px; text-decoration: none; color: var(--m-text-primary); }
.pcard--new:hover { border-color: var(--m-accent); }
.pcard--new__plus { width: 44px; height: 44px; border-radius: 12px; background: var(--m-bg-card); border: 1px solid var(--m-border); color: var(--m-accent); display: flex; align-items: center; justify-content: center; font-size: 1.5rem; }
.pcard--new__h { font-size: 1rem; font-weight: 700; }
.pcard--new__p { font-size: 0.78rem; color: var(--m-text-secondary); max-width: 30ch; }

.ph-drafts-link { margin: 14px 0 0; font-size: 0.85rem; }
.ph-drafts-link a { color: var(--m-accent-2); font-weight: 600; text-decoration: none; }

/* Draft pipelines nudge (findability) */
.ph-drafts-card { background: var(--m-bg-card); border: 1px solid color-mix(in srgb, var(--m-accent) 26%, var(--m-border)); border-left: 3px solid var(--m-accent); border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); padding: 14px 16px; margin-bottom: 16px; }
.ph-drafts-card__head { display: flex; align-items: flex-start; gap: 12px; }
.ph-drafts-card__ic { width: 34px; height: 34px; border-radius: 9px; background: var(--m-accent-soft); color: var(--m-accent); display: flex; align-items: center; justify-content: center; font-size: 0.95rem; flex: none; }
.ph-drafts-card__copy { min-width: 0; }
.ph-drafts-card__title { font-size: 0.9rem; font-weight: 700; letter-spacing: -0.01em; }
.ph-drafts-card__sub { font-size: 0.78rem; color: var(--m-text-tertiary); margin-top: 2px; }
.ph-drafts-list { list-style: none; margin: 12px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.ph-drafts-row { display: flex; align-items: center; gap: 10px; padding: 7px 4px; border-top: 1px solid var(--m-border); }
.ph-drafts-row__name { font-size: 0.85rem; font-weight: 600; color: var(--m-text-primary); margin-right: auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ph-drafts-row__cta { flex: none; text-decoration: none; }

/* Tailored starting points (deep-link chips into the pipeline wizard) */
.ph-sugg { margin: -4px 0 18px; }
.ph-sugg__h { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 9px; }
.ph-sugg__title { font-size: 0.78rem; font-weight: 700; letter-spacing: -0.01em; color: var(--m-text-secondary); }
.ph-sugg__sub { font-size: 0.74rem; color: var(--m-text-tertiary); }
.ph-sugg__chips { display: flex; flex-wrap: wrap; gap: 7px; }
.ph-sugg__chip {
  display: inline-flex; align-items: center; padding: 5px 12px; border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--m-accent) 30%, var(--m-border));
  background: var(--m-bg-card); color: var(--m-text-primary);
  font-size: 0.79rem; font-weight: 600; line-height: 1.3; text-decoration: none;
  transition: background .15s, border-color .15s, color .15s;
}
.ph-sugg__chip:hover { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-accent); }

/* Quick links pointer card (rail) */
.quicklinks-card { margin-top: 16px; background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); padding: 10px 8px; }
.quicklinks-card__h { font-size: 0.64rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: var(--m-text-tertiary); padding: 6px 10px 8px; }
.quicklink { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: var(--m-radius); font-size: 0.85rem; font-weight: 600; color: var(--m-text-secondary); text-decoration: none; }
.quicklink:hover { background: var(--m-bg-content); color: var(--m-accent); }
.quicklink span { margin-right: auto; }
.quicklink__ic { color: var(--m-accent); font-size: 0.95rem; }
.quicklink__go { opacity: 0.4; font-size: 0.75rem; }

/* Campaign strip */
.ph-camps { display: flex; flex-direction: column; gap: 10px; }
.camp { display: grid; grid-template-columns: 1.4fr repeat(3, 1fr) auto; gap: 18px; align-items: center; background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius); box-shadow: var(--m-shadow-card); padding: 14px 18px; }
@media (max-width: 720px) { .camp { grid-template-columns: 1fr; gap: 8px; } }
.camp__name { font-size: 0.88rem; font-weight: 700; letter-spacing: -0.01em; }
.camp__sub { font-size: 0.72rem; color: var(--m-text-tertiary); margin-top: 2px; }
.metric__top { display: flex; justify-content: space-between; align-items: baseline; }
.metric__k { color: var(--m-text-tertiary); font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; font-size: 0.58rem; }
.metric__v { font-weight: 800; font-size: 0.82rem; }
.metric__bar { height: 6px; border-radius: 999px; background: var(--m-border); overflow: hidden; margin-top: 5px; }
.metric__bar span { display: block; height: 100%; border-radius: 999px; }
.fill--open { background: var(--m-accent); }
.fill--click { background: var(--m-accent-2); }
.fill--reply { background: color-mix(in srgb, var(--m-accent-2) 70%, #000); }
.camp__act { justify-self: end; text-decoration: none; }

/* Usage rail */
.rail-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); padding: 16px 18px; }
.rail-card__h { font-size: 0.64rem; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; color: var(--m-text-tertiary); display: flex; align-items: center; margin-bottom: 14px; }
.rail-card__reset { margin-left: auto; font-weight: 600; letter-spacing: 0; text-transform: none; }
.rail-meter + .rail-meter { margin-top: 14px; }
.rail-meter__foot { display: flex; justify-content: space-between; align-items: center; font-size: 0.68rem; color: var(--m-text-tertiary); margin-top: 5px; }
.rail-meter__addon { color: var(--m-accent); font-weight: 600; text-decoration: none; border-bottom: 1px dotted var(--m-border-strong); }

.plan-card { margin-top: 16px; background: linear-gradient(135deg, var(--m-accent-hover, #3730A3), var(--m-accent)); border-radius: var(--m-radius-lg); box-shadow: var(--m-shadow-card); padding: 18px; color: #fff; }
.plan-card__top { display: flex; align-items: center; gap: 9px; }
.plan-card__name { font-size: 1.05rem; font-weight: 800; letter-spacing: -0.01em; }
.plan-card__badge { margin-left: auto; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; background: rgba(255,255,255,.2); border-radius: 999px; padding: 3px 9px; }
.plan-card__renew { font-size: 0.72rem; opacity: .82; margin-top: 6px; }
.plan-card__foot { display: flex; gap: 10px; margin-top: 14px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,.2); }
.plan-card__foot a { font-size: 0.78rem; font-weight: 600; color: #fff; text-decoration: none; }
.plan-card__foot a:hover { text-decoration: underline; }
</style>
